import { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from '../../primitives/Sheet';
import { Banner } from '../../primitives/Banner';
import { EmptyState } from '../../primitives/EmptyState';
import { Skeleton } from '../../primitives/Skeleton';
import { useActiveOrg } from '../../../lib/activeOrg';
import { KIND_CONFIG } from './kindConfig';
import { EMPTY_FILTERS } from './types';
import type { AttachKind, FilterState } from './types';
import { useAttachCandidates } from './useAttachCandidates';
import { useAttachMultiple } from './useAttachMultiple';
import { useAttachFacets } from './facets';
import { AttachFilterBar } from './AttachFilterBar';
import { AttachCandidatesTable } from './AttachCandidatesTable';

export interface AttachExistingModalProps {
  open: boolean;
  kind: AttachKind;
  projectId: string;
  onClose: () => void;
  onDone: () => void;
}

const NOUN: Record<AttachKind, string> = { documents: 'documents', epics: 'epics', 'work-items': 'work items' };
const NOUN_TITLE: Record<AttachKind, string> = { documents: 'Documents', epics: 'Epics', 'work-items': 'Work items' };

export function AttachExistingModal({ open, kind, projectId, onClose, onDone }: AttachExistingModalProps) {
  const config = KIND_CONFIG[kind];
  const { activeOrgId } = useActiveOrg();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // EC-11: capture the org at open; if the active org changes while open, close.
  const orgAtOpen = useRef<string | null>(activeOrgId);
  useEffect(() => {
    if (!open) return;
    if (orgAtOpen.current !== null && activeOrgId !== orgAtOpen.current) onClose();
  }, [activeOrgId, open, onClose]);

  // On (re)open, reset per-session state and re-capture the org.
  useEffect(() => {
    if (!open) return;
    orgAtOpen.current = activeOrgId;
    setFilters(EMPTY_FILTERS);
    setErrorBanner(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);

  const sort = useMemo(
    () => ({ key: config.defaultSort.columnKey, dir: config.defaultSort.direction }),
    [config]
  );
  const { rows, total, loading, error, refetch } = useAttachCandidates({
    kind,
    activeOrgId,
    projectId,
    enabled: open,
    filters,
    sort,
    page: 1,
  });
  const { attach } = useAttachMultiple({ kind, activeOrgId, projectId });
  const { typeFacet, ownerFacet } = useAttachFacets(kind, activeOrgId, open);

  const onAttach = async (ids: string[]) => {
    if (ids.length === 0) return;
    setErrorBanner(null);
    const summary = await attach(ids);
    if (summary.failed.length === 0) {
      onDone();
      onClose();
      return;
    }
    if (summary.succeeded.length > 0) onDone();
    refetch();
    setErrorBanner(
      `Attached ${summary.succeeded.length} of ${ids.length} — ${summary.failed.length} failed. The failed items are still listed; try again.`
    );
  };

  const bodyState: 'loading' | 'error' | 'empty' | 'no-match' | 'ok' = loading
    ? 'loading'
    : error
      ? 'error'
      : total === 0
        ? 'empty'
        : rows.length === 0
          ? 'no-match'
          : 'ok';

  return (
    <Sheet
      open={open}
      onClose={onClose}
      wide
      title={
        <span className="pn-attach-existing-head">
          <span className="pn-eyebrow"><span className="pn-eyebrow-sq" aria-hidden="true" /> Attach to project</span>
          <span className="pn-attach-existing-noun">{NOUN_TITLE[kind]}</span>
        </span>
      }
      ariaLabel={config.title}
      dataTestId="attach-existing-modal"
      header={
        <AttachFilterBar
          config={config}
          filters={filters}
          onFilters={setFilters}
          typeFacet={typeFacet}
          ownerFacet={ownerFacet}
          shown={rows.length}
          total={total}
        />
      }
    >
      {errorBanner ? (
        <Banner kind="error" title="Some items couldn’t be attached">
          {errorBanner}
        </Banner>
      ) : null}
      {bodyState === 'loading' ? (
        <div className="pn-attach-existing-skeleton" data-testid="attach-existing-loading" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={36} />
          ))}
        </div>
      ) : bodyState === 'error' ? (
        <Banner kind="error" title={`Couldn’t load ${NOUN[kind]}`} data-testid="attach-existing-error">
          {error}
        </Banner>
      ) : bodyState === 'empty' ? (
        <EmptyState data-testid="attach-existing-empty">No {NOUN[kind]} available to attach.</EmptyState>
      ) : bodyState === 'no-match' ? (
        <EmptyState data-testid="attach-existing-no-match">No {NOUN[kind]} match your search and filters.</EmptyState>
      ) : (
        <AttachCandidatesTable config={config} rows={rows} onAttach={onAttach} />
      )}
    </Sheet>
  );
}
